// src/pages/Home/StockHistory.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import config from '../../config';

export default function StockHistoryPage() {
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const token = sessionStorage.getItem('token');

  const [transactions, setTransactions] = useState([]);
  const [storeItems, setStoreItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [filterStore, setFilterStore] = useState('');
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [txnRes, storeRes] = await Promise.all([
          axios.get(
            `${config.API_BASE_URL}/home/GetTransactions/${company.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
          axios.get(`${config.API_BASE_URL}/home/GetStoreItems/${company.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setTransactions(txnRes.data || []);
        setStoreItems(storeRes.data || []);
      } catch {
        setAlert({ type: 'error', msg: 'Failed to load transactions' });
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const filtered = transactions.filter(
    (t) =>
      (!filterStore || String(t.storeItemId) === String(filterStore)) &&
      (!filterType || t.transactionType === filterType),
  );

  const paged = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      filtered.map((t, i) => ({
        '#': i + 1,
        'Store Item': t.storeItemName,
        Unit: t.storeItemUnit || '',
        Type: t.transactionType,
        'Qty Change': t.quantity,
        'Balance After': t.balanceAfter,
        'Order ID': t.orderId,
        Remarks: t.remarks,
        Date: new Date(t.createdDate).toLocaleString('en-GB'),
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, 'StockHistory.xlsx');
  };

  const txnColor = (type) => {
    if (type === 'ORDER_DEDUCTION') return 'error';
    if (type === 'MANUAL_ADD') return 'success';
    if (type === 'ADJUSTMENT') return 'warning';
    return 'default';
  };

  const txnLabel = (type) => {
    if (type === 'ORDER_DEDUCTION') return 'Order Deduction';
    if (type === 'MANUAL_ADD') return 'Stock Added';
    if (type === 'ADJUSTMENT') return 'Adjustment';
    return (type || '').replace(/_/g, ' ');
  };

  return (
    <Container maxWidth='xl' sx={{ mt: 4, mb: 6 }}>
      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      {/* Summary cards */}
      <Box display='flex' gap={2} flexWrap='wrap' mb={3}>
        {storeItems.slice(0, 6).map((s) => (
          <Card
            key={s.id}
            sx={{
              minWidth: 160,
              borderRadius: 2,
              border:
                s.currentStock <= s.minStockAlert
                  ? '2px solid #EF4444'
                  : '1px solid #E4E7F0',
            }}>
            <CardContent sx={{ p: '12px !important' }}>
              <Typography fontSize='0.75rem' color='text.secondary'>
                {s.storeName}
              </Typography>
              <Typography
                fontSize='1.2rem'
                fontWeight={800}
                color={
                  s.currentStock <= s.minStockAlert ? 'error' : 'success.main'
                }>
                {s.currentStock}{' '}
                <span style={{ fontSize: '0.7rem' }}>{s.unit}</span>
              </Typography>
              {s.currentStock <= s.minStockAlert && (
                <Chip label='Low Stock' size='small' color='error' />
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
            flexWrap='wrap'
            gap={1}>
            <Typography variant='h6' fontWeight={600}>
              Stock Transaction History
            </Typography>
            <Box display='flex' gap={1} flexWrap='wrap'>
              <FormControl size='small' sx={{ minWidth: 180 }}>
                <InputLabel>Filter Store Item</InputLabel>
                <Select
                  label='Filter Store Item'
                  value={filterStore}
                  onChange={(e) => setFilterStore(e.target.value)}>
                  <MenuItem value=''>All</MenuItem>
                  {storeItems.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.storeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size='small' sx={{ minWidth: 160 }}>
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  label='Transaction Type'
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}>
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='ORDER_DEDUCTION'>Order Deduction</MenuItem>
                  <MenuItem value='MANUAL_ADD'>Stock Added</MenuItem>
                  <MenuItem value='ADJUSTMENT'>Adjustment</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant='outlined'
                startIcon={<FileDownloadIcon />}
                onClick={exportExcel}>
                Export
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box textAlign='center' py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {[
                        '#',
                        'Store Item',
                        'Type',
                        'Qty Change',
                        'Balance After',
                        'Order ID',
                        'Remarks',
                        'Date',
                      ].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((t, i) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t.storeItemName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={txnLabel(t.transactionType)}
                            size='small'
                            color={txnColor(t.transactionType)}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: t.quantity < 0 ? '#EF4444' : '#10B981',
                          }}>
                          {t.quantity > 0 ? '+' : ''}
                          {Number(t.quantity).toFixed(
                            t.storeItemUnit === 'bottle' ? 2 : 0,
                          )}{' '}
                          <Typography
                            component='span'
                            sx={{
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                            }}>
                            {t.storeItemUnit || ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {Number(t.balanceAfter).toFixed(
                            t.storeItemUnit === 'bottle' ? 2 : 0,
                          )}{' '}
                          <Typography
                            component='span'
                            sx={{
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                            }}>
                            {t.storeItemUnit || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {t.orderId > 0 ? `#${t.orderId}` : '-'}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: '0.72rem',
                            color: 'text.secondary',
                            maxWidth: 200,
                          }}>
                          {t.remarks}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.72rem' }}>
                          {new Date(t.createdDate).toLocaleString('en-GB')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 15, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
